import { useMemo, useRef } from 'react'
import { api } from '@ppwr/convex/_generated/api'
import { useQuery } from 'convex/react'
import { useGameState } from '@/screens/GameContainer/useGameState'
import { useAnimationFrame } from '../../components/GameAssets/hooks/useAnimationFrame'

/**
 * Remaining ms for a turn/round countdown, measured from a fixed start anchor and
 * capped to the configured duration so it never displays above the total.
 */
export function calculateTurnRemainingMs(totalDuration: number, startAnchor: number, now: number): number {
  return Math.max(0, Math.min(totalDuration, totalDuration - (now - startAnchor)))
}

export function usePlayerTimer() {
  const { game } = useGameState()
  // Get all pending schedules for this game
  const schedules = useQuery(api.games.queries.getGameSchedules, game?._id ? { gameId: game._id } : 'skip')

  // Server state (from Convex subscription)
  const isPaused = game?.state === 'playing' ? Boolean(game.pausedAt) : false
  const pausedAt = game?.state === 'playing' ? game.pausedAt : null
  const currentPlayerId = game?.state === 'playing' ? game.currentPlayerId : null
  const turnDuration = game?.state === 'playing' ? game.turnDuration : 60000
  const turnStartedAt = game?.state === 'playing' ? game.turnStartedAt : null

  // Find active schedule for current player (prioritize turnTimer over botAction)
  // Also check for roundProgression schedules (no playerId requirement)
  const activeSchedule = useMemo(() => {
    if (!schedules) return null

    // First, try to find player-specific schedule (turnTimer or botAction)
    if (currentPlayerId) {
      const playerSchedule = schedules.find(
        (s) => (s.scheduleType === 'turnTimer' || s.scheduleType === 'botAction') && s.playerId === currentPlayerId
      )
      if (playerSchedule) return playerSchedule
    }

    // If no player schedule, check for roundProgression
    const roundSchedule = schedules.find((s) => s.scheduleType === 'roundProgression')
    return roundSchedule || null
  }, [schedules, currentPlayerId])

  const now = useAnimationFrame(!isPaused)

  // Client-clock anchor for the active schedule's start. Captured once per
  // schedule (keyed by _id) so server/client clock skew can't inflate the
  // countdown or stall the progress bar at the top of a turn.
  const anchorRef = useRef<{ key: string; value: number } | null>(null)

  // Calculate timer from actual schedule data OR paused state
  const timerData = useMemo(() => {
    // PAUSED STATE: Show frozen elapsed/remaining breakdown
    if (isPaused && pausedAt && turnStartedAt) {
      const elapsed = pausedAt - turnStartedAt
      const remaining = turnDuration - elapsed

      return {
        isPaused: true,
        remaining: Math.max(0, remaining),
        elapsed: Math.max(0, elapsed),
        // Progress bar shows remaining time (1.0 = full, 0.0 = empty)
        progress: Math.max(0, Math.min(1, remaining / turnDuration)),
        scheduledFor: pausedAt + remaining,
        totalDuration: turnDuration,
        // Bot dual timer fields (inferred from game state during pause)
        actualBotDelay: undefined,
        uiDisplayDuration: turnDuration,
        uiDisplayElapsed: elapsed
      }
    }

    // ACTIVE STATE: Use real schedule data
    if (!activeSchedule) return null

    // Total configured duration for this schedule type.
    const totalDuration =
      activeSchedule.scheduleType === 'roundProgression' ? activeSchedule.originalDelay : turnDuration

    // `scheduledFor` (and the implied start time) are *server* timestamps, while
    // `now` is this device's clock. On a device whose clock runs behind the
    // server the turn appears to start in the future, which makes the raw
    // `scheduledFor - now` overshoot past the duration and the progress bar stall
    // at full. Anchor the start to this device's clock once per schedule so
    // elapsed never goes negative: the countdown then starts at the full duration
    // and ticks down immediately. Re-anchors automatically when a new schedule
    // (next turn, resume, or round) appears, since each carries a fresh _id.
    const serverStart = activeSchedule.scheduledFor - totalDuration
    if (!anchorRef.current || anchorRef.current.key !== activeSchedule._id) {
      anchorRef.current = { key: activeSchedule._id, value: Math.min(serverStart, now) }
    }
    const startAnchor = anchorRef.current.value

    const remaining = calculateTurnRemainingMs(totalDuration, startAnchor, now)
    const elapsed = totalDuration - remaining
    const progress = totalDuration > 0 ? Math.max(0, Math.min(1, remaining / totalDuration)) : 0

    return {
      isPaused: false,
      remaining,
      elapsed,
      progress,
      scheduleType: activeSchedule.scheduleType,
      scheduledFor: activeSchedule.scheduledFor,
      totalDuration,
      // Bot dual timer data (inferred elsewhere; not derived here)
      actualBotDelay: undefined as number | undefined,
      uiDisplayDuration: undefined as number | undefined,
      uiDisplayElapsed: undefined as number | undefined
    }
  }, [activeSchedule, now, isPaused, pausedAt, turnStartedAt, turnDuration])

  return timerData
}
